import { ReportColumn, ReportDefinition } from '../models/report-definition';
import { ReportFilterConfig } from '../models/report-filtering';
import { ReportRowVm } from '../models/report-row-vm';
import {
   asNumberOrNull,
   asString,
   formatCurrency,
   formatDate
} from '../utils/report-formatting.utils';
import { getReportArray } from '../utils/report-json.utils';
import { getRealTyallsRowId } from '../utils/report-row-id.utils';

const LAYOUT_SUMMARY = 'summary';
const LAYOUT_FULL_DETAILS = 'full-details';

type NoShowSourceRow = {
   TyallsRowId?: unknown;
   Name?: unknown;
   RateCode?: unknown;
   RoomType?: unknown;
   MarketCode?: unknown;
   RateAmount?: unknown;
   RoomNumber?: unknown;
   ArrivalDate?: unknown;
   DepositPaid?: unknown;
   RoomsNumber?: unknown;
   DepartureDate?: unknown;
   PaymentMethod?: unknown;
   ReservationType?: unknown;
   ReservationNights?: unknown;
   ConfirmationNumber?: unknown;
   PotentialStayRevenue?: unknown;
   CompanyTravelAgentSource?: unknown;
};

type NoShowDisplayRow = {
   rowId: string;
   hasRealTyallsRowId: boolean;

   roomNumber: string;
   name: string;
   companyTravelAgentSource: string;

   arrivalDate: string;
   arrivalDateRaw: string;

   departureDate: string;
   departureDateRaw: string;

   roomType: string;
   marketCode: string;
   reservationType: string;
   rateCode: string;

   rateAmount: string;
   rateAmountRaw: number | null;

   roomsNumber: string;
   roomsNumberRaw: number | null;

   reservationNights: string;
   reservationNightsRaw: number | null;

   potentialStayRevenue: string;
   potentialStayRevenueRaw: number | null;

   depositPaid: string;
   depositPaidRaw: number | null;

   confirmationNumber: string;
   paymentMethod: string;
};

function getText(value: unknown): string {
   return asString(value).trim();
}

function getNumber(value: unknown): number | null {
   return asNumberOrNull(value, {
      removeGroupingSeparators: true
   });
}

function getNoShowRows(reportJson: unknown): NoShowSourceRow[] {
   return getReportArray<NoShowSourceRow>(reportJson, 'NoShows');
}

function getFallbackRowId(row: NoShowSourceRow): string {
   return [
      'no-show',
      getText(row.ConfirmationNumber),
      getText(row.RoomNumber),
      getText(row.ArrivalDate),
      getText(row.Name)
   ].join(':');
}

function normaliseRows(reportJson: unknown): NoShowDisplayRow[] {
   return getNoShowRows(reportJson)
      .map((row) => {
         const realTyallsRowId = getRealTyallsRowId(row);

         const rateAmountRaw = getNumber(row.RateAmount);
         const roomsNumberRaw = getNumber(row.RoomsNumber);
         const reservationNightsRaw = getNumber(row.ReservationNights);
         const potentialStayRevenueRaw = getNumber(row.PotentialStayRevenue);
         const depositPaidRaw = getNumber(row.DepositPaid);

         return {
            rowId: realTyallsRowId || getFallbackRowId(row),
            hasRealTyallsRowId: realTyallsRowId !== '',

            roomNumber: getText(row.RoomNumber),
            name: getText(row.Name),
            companyTravelAgentSource: getText(row.CompanyTravelAgentSource) || '—',

            arrivalDate: formatDate(row.ArrivalDate),
            arrivalDateRaw: getText(row.ArrivalDate),

            departureDate: formatDate(row.DepartureDate),
            departureDateRaw: getText(row.DepartureDate),

            roomType: getText(row.RoomType),
            marketCode: getText(row.MarketCode),
            reservationType: getText(row.ReservationType),
            rateCode: getText(row.RateCode),

            rateAmount: formatCurrency(rateAmountRaw),
            rateAmountRaw,

            roomsNumber: roomsNumberRaw === null ? '' : String(roomsNumberRaw),
            roomsNumberRaw,

            reservationNights:
               reservationNightsRaw === null ? '' : String(reservationNightsRaw),
            reservationNightsRaw,

            potentialStayRevenue: formatCurrency(potentialStayRevenueRaw),
            potentialStayRevenueRaw,

            depositPaid: formatCurrency(depositPaidRaw),
            depositPaidRaw,

            confirmationNumber: getText(row.ConfirmationNumber),
            paymentMethod: getText(row.PaymentMethod)
         };
      })
      .sort((a, b) => {
         return (
            a.arrivalDateRaw.localeCompare(b.arrivalDateRaw) ||
            a.roomNumber.localeCompare(b.roomNumber, undefined, {
               numeric: true
            }) ||
            a.name.localeCompare(b.name)
         );
      });
}

function flattenRows(reportJson: unknown): ReportRowVm[] {
   return normaliseRows(reportJson).map((row) => ({
      rowId: row.rowId,

      /*
       * Comments and manual highlighting require a generated
       * TyallsRowId. Fallback IDs remain display-only.
       */
      isAnnotatable: row.hasRealTyallsRowId,

      cells: {
         rowType: 'noShow',

         roomNumber: row.roomNumber,
         name: row.name,
         companyTravelAgentSource: row.companyTravelAgentSource,

         arrivalDate: row.arrivalDate,
         arrivalDateRaw: row.arrivalDateRaw,

         departureDate: row.departureDate,
         departureDateRaw: row.departureDateRaw,

         roomType: row.roomType,
         marketCode: row.marketCode,
         reservationType: row.reservationType,
         rateCode: row.rateCode,

         rateAmount: row.rateAmount,
         rateAmountRaw: row.rateAmountRaw,

         roomsNumber: row.roomsNumber,
         roomsNumberRaw: row.roomsNumberRaw,

         reservationNights: row.reservationNights,
         reservationNightsRaw: row.reservationNightsRaw,

         potentialStayRevenue: row.potentialStayRevenue,
         potentialStayRevenueRaw: row.potentialStayRevenueRaw,

         depositPaid: row.depositPaid,
         depositPaidRaw: row.depositPaidRaw,

         confirmationNumber: row.confirmationNumber,
         paymentMethod: row.paymentMethod
      }
   }));
}

const SUMMARY_COLUMNS: ReportColumn[] = [
   {
      key: 'roomNumber',
      header: 'ROOM',
      width: '70px',
      sort: {
         type: 'string'
      }
   },
   {
      key: 'name',
      header: 'GUEST NAME',
      width: '160px',
      maxChars: 60,
      sort: {
         type: 'string'
      }
   },
   {
      key: 'arrivalDate',
      header: 'ARRIVAL',
      width: '95px',
      sort: {
         type: 'date',
         getValue: (row) => row.cells['arrivalDateRaw']
      }
   },
   {
      key: 'departureDate',
      header: 'DEPARTURE',
      width: '95px',
      sort: {
         type: 'date',
         getValue: (row) => row.cells['departureDateRaw']
      }
   },
   {
      key: 'reservationNights',
      header: 'NIGHTS',
      width: '65px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['reservationNightsRaw']
      }
   },
   {
      key: 'rateAmount',
      header: 'RATE',
      width: '100px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['rateAmountRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'potentialStayRevenue',
      header: 'POTENTIAL REVENUE',
      width: '135px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['potentialStayRevenueRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'confirmationNumber',
      header: 'CONFIRMATION NO.',
      width: '125px',
      sort: {
         type: 'string'
      }
   }
];

const FULL_DETAILS_COLUMNS: ReportColumn[] = [
   {
      key: 'roomNumber',
      header: 'ROOM',
      width: '85px',
      sort: { type: 'string' }
   },
   {
      key: 'name',
      header: 'GUEST NAME',
      width: '190px',
      maxChars: 80,
      sort: { type: 'string' }
   },
   {
      key: 'companyTravelAgentSource',
      header: 'COMPANY / TA / SOURCE',
      width: '260px',
      maxChars: 100,
      sort: { type: 'string' }
   },
   {
      key: 'arrivalDate',
      header: 'ARRIVAL',
      width: '110px',
      sort: {
         type: 'date',
         getValue: (row) => row.cells['arrivalDateRaw']
      }
   },
   {
      key: 'departureDate',
      header: 'DEPARTURE',
      width: '110px',
      sort: {
         type: 'date',
         getValue: (row) => row.cells['departureDateRaw']
      }
   },
   {
      key: 'roomType',
      header: 'ROOM TYPE',
      width: '105px',
      sort: { type: 'string' }
   },
   {
      key: 'marketCode',
      header: 'MARKET',
      width: '95px',
      sort: { type: 'string' }
   },
   {
      key: 'reservationType',
      header: 'RES. TYPE',
      width: '105px',
      sort: { type: 'string' }
   },
   {
      key: 'rateCode',
      header: 'RATE CODE',
      width: '110px',
      sort: { type: 'string' }
   },
   {
      key: 'rateAmount',
      header: 'RATE',
      width: '110px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['rateAmountRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'roomsNumber',
      header: 'ROOMS',
      width: '80px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['roomsNumberRaw']
      }
   },
   {
      key: 'reservationNights',
      header: 'NIGHTS',
      width: '80px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['reservationNightsRaw']
      }
   },
   {
      key: 'potentialStayRevenue',
      header: 'POTENTIAL REVENUE',
      width: '155px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['potentialStayRevenueRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'depositPaid',
      header: 'DEPOSIT PAID',
      width: '130px',
      sort: {
         type: 'number',
         getValue: (row) => row.cells['depositPaidRaw'],
         defaultDirection: 'desc'
      }
   },
   {
      key: 'confirmationNumber',
      header: 'CONFIRMATION NO.',
      width: '155px',
      sort: { type: 'string' }
   },
   {
      key: 'paymentMethod',
      header: 'PAYMENT',
      width: '100px',
      sort: { type: 'string' }
   }
];

const NO_SHOWS_FILTER: ReportFilterConfig = {
   fields: [
      {
         key: 'roomNumber',
         label: 'Room',
         type: 'string'
      },
      {
         key: 'name',
         label: 'Guest Name',
         type: 'string'
      },
      {
         key: 'companyTravelAgentSource',
         label: 'Company / Travel Agent / Source',
         type: 'string'
      },
      {
         key: 'arrivalDate',
         label: 'Arrival Date',
         type: 'date',
         getValue: (row) => row.cells['arrivalDateRaw']
      },
      {
         key: 'departureDate',
         label: 'Departure Date',
         type: 'date',
         getValue: (row) => row.cells['departureDateRaw']
      },
      {
         key: 'roomType',
         label: 'Room Type',
         type: 'string'
      },
      {
         key: 'marketCode',
         label: 'Market Code',
         type: 'string'
      },
      {
         key: 'reservationType',
         label: 'Reservation Type',
         type: 'string'
      },
      {
         key: 'rateCode',
         label: 'Rate Code',
         type: 'string'
      },
      {
         key: 'rateAmount',
         label: 'Rate Amount',
         type: 'number',
         getValue: (row) => row.cells['rateAmountRaw']
      },
      {
         key: 'roomsNumber',
         label: 'Rooms',
         type: 'number',
         getValue: (row) => row.cells['roomsNumberRaw']
      },
      {
         key: 'reservationNights',
         label: 'Reservation Nights',
         type: 'number',
         getValue: (row) => row.cells['reservationNightsRaw']
      },
      {
         key: 'potentialStayRevenue',
         label: 'Potential Stay Revenue',
         type: 'number',
         getValue: (row) => row.cells['potentialStayRevenueRaw']
      },
      {
         key: 'depositPaid',
         label: 'Deposit Paid',
         type: 'number',
         getValue: (row) => row.cells['depositPaidRaw']
      },
      {
         key: 'confirmationNumber',
         label: 'Confirmation Number',
         type: 'string'
      },
      {
         key: 'paymentMethod',
         label: 'Payment Method',
         type: 'string'
      }
   ]
};

export const NO_SHOWS_DEFINITION: ReportDefinition = {
   reportType: 'NoShows',
   version: 2,
   title: 'No Shows',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'No Shows Report'
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['noShow']
   },

   annotations: {
      enabled: true,
      selectableRowTypes: ['noShow']
   },

   tableMinWidth: '1040px',
   columns: SUMMARY_COLUMNS,
   flattenRows,
   filter: NO_SHOWS_FILTER,

   layouts: [
      {
         id: LAYOUT_SUMMARY,
         label: 'Summary',
         tableMinWidth: '1040px',

         columns: SUMMARY_COLUMNS,
         flattenRows,
         filter: NO_SHOWS_FILTER,

         rowDetails: {
            enabled: true,
            selectableRowTypes: ['noShow'],
            fields: [
               {
                  key: 'companyTravelAgentSource',
                  label: 'Company / Travel Agent / Source'
               },
               {
                  key: 'roomType',
                  label: 'Room Type'
               },
               {
                  key: 'marketCode',
                  label: 'Market Code'
               },
               {
                  key: 'reservationType',
                  label: 'Reservation Type'
               },
               {
                  key: 'rateCode',
                  label: 'Rate Code'
               },
               {
                  key: 'roomsNumber',
                  label: 'Rooms'
               },
               {
                  key: 'depositPaid',
                  label: 'Deposit Paid'
               },
               {
                  key: 'paymentMethod',
                  label: 'Payment Method'
               }
            ]
         }
      },
      {
         id: LAYOUT_FULL_DETAILS,
         label: 'Full Details',
         tableMinWidth: '1900px',

         columns: FULL_DETAILS_COLUMNS,
         flattenRows,
         filter: NO_SHOWS_FILTER
      }
   ]
};
